/**
 * useLoadStatuses(uids) — the Load status labels of the tickets a page lists (ST-00218,
 * lm-status). Asks POST /api/load-matching/ticket-status once per distinct set of uids (cached
 * per uid in services/loadStatus), cancels a stale request when the list changes.
 *
 *   const { enabled, statuses } = useLoadStatuses(rows.map((r) => r.ticketUid));
 *   enabled === true  → show the "Load status" column (statuses[uid] may be absent: blank cell)
 *   enabled === false → LOAD_MATCHING=off: hide the column
 *   enabled === null  → not known yet / lookup failed: hide the column
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchLoadStatuses, knownEnabled, type LoadStatusResult } from '../services/loadStatus';

export interface UseLoadStatuses extends LoadStatusResult {
  loading: boolean;
}

export function useLoadStatuses(
  uids: readonly (string | null | undefined)[],
  { skip = false }: { skip?: boolean } = {}
): UseLoadStatuses {
  const key = useMemo(
    () =>
      [...new Set(uids.filter((u): u is string => typeof u === 'string' && !!u))]
        .sort()
        .join('\u0001'),
    [uids]
  );
  // A page opened after another one already asked starts with the known answer (no pop-in).
  const [state, setState] = useState<UseLoadStatuses>(() => ({
    enabled: knownEnabled(),
    statuses: {},
    loading: false,
  }));
  useEffect(() => {
    if (skip || !key) {
      setState((s) => (s.loading ? { ...s, loading: false } : s));
      return undefined;
    }
    const ctrl = new AbortController();
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    fetchLoadStatuses(key.split('\u0001'), { signal: ctrl.signal })
      .then((r) => {
        if (live) setState({ ...r, loading: false });
      })
      .catch(() => {
        if (live) setState((s) => ({ ...s, loading: false }));
      });
    return () => {
      live = false;
      ctrl.abort();
    };
  }, [key, skip]);
  return state;
}

export default useLoadStatuses;
